/**
 * Utility function to ensure image URLs are absolute
 * Handles both relative URLs from the API and absolute URLs
 */

import { buildApiUrl, getApiBaseUrl } from './appBase';

const RAW_API_BASE_URL = getApiBaseUrl();

/**
 * Trim trailing slashes so checks like `endsWith('/api')` stay reliable
 * (e.g. `/api/` must still be treated as “already includes /api”).
 */
const API_BASE_URL = String(RAW_API_BASE_URL).trim().replace(/\/+$/, '');

/** API base may already end with `/api` (e.g. `/api` on hortera.org). */
function apiBaseAlreadyIncludesApiPath(): boolean {
	return /\/api$/i.test(API_BASE_URL);
}

/** Fix `…/api/api/uploads/…` from bad DB joins or missing leading slash on `api/…`. */
function collapseDoubleApiSegment(url: string): string {
	return url.replace(/\/api\/api(\/|$)/g, '/api$1');
}

/** Early migration used a non-resolving `.hetzner.cloud` host in object-storage config. */
function fixObjectStorageHost(url: string): string {
	return url.replace(/your-objectstorage\.hetzner\.cloud/g, 'your-objectstorage.com');
}

/**
 * Convert a relative or absolute image URL to an absolute URL
 * @param url - The URL (can be relative like /api/uploads/…, api/uploads/…, or absolute)
 * @returns Absolute URL that can be used in img src
 */
export function getImageUrl(url: string | undefined | null): string | undefined {
	if (!url) return undefined;

	let fixedUrl = fixObjectStorageHost(url.trim());

	// DB sometimes stores "api/uploads/…" without a leading slash — would become …/api + api/uploads…
	if (!fixedUrl.includes('://') && /^api\//i.test(fixedUrl)) {
		fixedUrl = `/${fixedUrl}`;
	}

	// If URL ends with incomplete extension, try to fix common truncation issues
	if (fixedUrl.endsWith('.j') && !fixedUrl.endsWith('.jpg') && !fixedUrl.endsWith('.jpeg')) {
		const baseUrl = fixedUrl.replace(/\.j$/, '');
		if (baseUrl.match(/[a-f0-9_]+$/)) {
			fixedUrl = baseUrl + '.jpg';
		}
	} else if (fixedUrl.endsWith('.pn') && !fixedUrl.endsWith('.png')) {
		const baseUrl = fixedUrl.replace(/\.pn$/, '');
		if (baseUrl.match(/[a-f0-9_]+$/)) {
			fixedUrl = baseUrl + '.png';
		}
	}

	if (fixedUrl.startsWith('http://localhost:') || fixedUrl.startsWith('https://localhost:')) {
		const urlObj = new URL(fixedUrl);
		fixedUrl = urlObj.pathname;
	} else if (fixedUrl.startsWith('http://') || fixedUrl.startsWith('https://')) {
		fixedUrl = fixObjectStorageHost(fixedUrl.replace(/\/api\/api\//g, '/api/'));
		return collapseDoubleApiSegment(fixedUrl);
	}

	if (fixedUrl.startsWith('uploads/')) {
		fixedUrl = '/api/' + fixedUrl;
	}

	// Some rows already include a leading slash for uploaded files (`/uploads/...`).
	// Those should still resolve through the PHP API, unlike public frontend assets.
	if (fixedUrl.startsWith('/uploads/')) {
		fixedUrl = `/api${fixedUrl}`;
	}

	if (fixedUrl.startsWith('/api/uploads')) {
		if (apiBaseAlreadyIncludesApiPath()) {
			const cleanUrl = fixedUrl.replace(/^\/api/, '');
			return collapseDoubleApiSegment(`${API_BASE_URL}${cleanUrl}`);
		}
		return collapseDoubleApiSegment(`${API_BASE_URL}${fixedUrl}`);
	}

	if (fixedUrl.startsWith('/api/')) {
		if (apiBaseAlreadyIncludesApiPath()) {
			const cleanUrl = fixedUrl.replace(/^\/api/, '');
			return collapseDoubleApiSegment(`${API_BASE_URL}${cleanUrl}`);
		}
		return collapseDoubleApiSegment(`${API_BASE_URL}${fixedUrl}`);
	}

	if (fixedUrl.startsWith('/web/')) {
		return collapseDoubleApiSegment(fixedUrl);
	}

	if (fixedUrl.startsWith('/')) {
		// Leading-slash paths can be same-origin public assets such as
		// `/questionnaire/emptygarden1.png`; do not rewrite those under `/api`.
		return collapseDoubleApiSegment(fixedUrl);
	}

	if (!fixedUrl.includes('://') && !fixedUrl.startsWith('/')) {
		const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
		return collapseDoubleApiSegment(`${base}/${fixedUrl}`);
	}

	return collapseDoubleApiSegment(fixedUrl);
}

/**
 * URL suitable for fetch() when embedding images in PDFs (same-origin proxy avoids object-storage CORS).
 */
export function getPdfFetchableImageUrl(url: string | undefined | null): string | undefined {
	const resolved = getImageUrl(url);
	if (!resolved) return undefined;
	if (resolved.startsWith('data:')) return resolved;
	return buildApiUrl(`/image-proxy?src=${encodeURIComponent(resolved)}`);
}
